import type { QueryClient } from '@tanstack/react-query';

import { courseQueryKey } from '@/features/courses/useCourse';
import { coursesQueryKey } from '@/features/courses/useCourses';
import { isProfileComplete } from '@/features/profile/profileService';
import {
  profileQueryKey,
  type ProfileData,
  type ProfileService,
} from '@/features/profile/profileTypes';

// How long to wait before the second, third and fourth read of the profile.
export const READ_BACK_WAITS_MS = [250, 500, 1000] as const;

// The invite was accepted, but the invitee's profile never showed the course.
export class CourseNotReadyError extends Error {
  constructor() {
    super('The course isn’t showing on your account yet. Try again in a moment.');
    this.name = 'CourseNotReadyError';
  }
}

interface ConfirmJoinedCourseInput {
  courseId: string;
  profileService: Pick<ProfileService, 'getProfile'>;
  queryClient: QueryClient;
  userId: string;
  wait?: (ms: number) => Promise<void>;
}

function pause(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

// What the route guards need before they let someone into the course: a named
// profile (ProfileCompletionRoute) that lists it (CourseAccessRoute).
function showsCourse(profile: ProfileData, courseId: string) {
  return isProfileComplete(profile) && profile.courseIds.includes(courseId);
}

// Cancels any read of the profile that is in flight and drops the cached copy.
// The next page to need the profile then loads it and waits for the answer,
// instead of acting on an old one.
function forgetProfile(queryClient: QueryClient, userId: string) {
  queryClient
    .getQueryCache()
    .find({ exact: true, queryKey: profileQueryKey(userId) })
    ?.reset();
}

// Run after an invite is accepted and before the course is opened. It makes
// the app's copy of the invitee's profile match the server.
//
// The profile is loaded the moment someone signs in, so for a new invitee a
// read is usually out before the invite is accepted. Invalidating is not
// enough then: a query that is already fetching and has no data answers a
// refetch with the fetch in flight, and that pre-accept answer (no name, no
// course) is cached as fresh. So this reads the profile itself, then forgets
// whatever the app had or was still waiting for and caches what it read.
//
// It reads more than once because a read can still come back old: the service
// worker answers a profile read from its own cache when the network is slow.
//
// Accepting writes the course's staff list, the user's course list and the
// profile in one transaction, so a profile that lists the course confirms all
// three.
export async function confirmJoinedCourse({
  courseId,
  profileService,
  queryClient,
  userId,
  wait = pause,
}: ConfirmJoinedCourseInput): Promise<ProfileData> {
  let confirmed: ProfileData | null = null;

  for (let read = 0; read <= READ_BACK_WAITS_MS.length && !confirmed; read += 1) {
    if (read > 0) {
      await wait(READ_BACK_WAITS_MS[read - 1]);
    }

    try {
      const profile = await profileService.getProfile({ userId });

      if (showsCourse(profile, courseId)) {
        confirmed = profile;
      }
    } catch {
      // A failed read counts as not ready; the next one may get through.
    }
  }

  // Course lists loaded before the accept are out of date either way.
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: coursesQueryKey(userId) }),
    queryClient.invalidateQueries({ queryKey: courseQueryKey(courseId) }),
  ]);

  // What the app has cached, and any read still out, may predate the accept.
  // Only a confirmed profile is kept.
  forgetProfile(queryClient, userId);

  if (!confirmed) {
    throw new CourseNotReadyError();
  }

  queryClient.setQueryData(profileQueryKey(userId), confirmed);

  return confirmed;
}
